import { z } from 'zod';
import { countryCodeSchema, emailSchema, slugSchema, uuidSchema } from './common';
import { featureKeyEnum } from '../features';

const domainSchema = z
  .string()
  .min(4)
  .max(253)
  .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/, 'invalid_domain');

export const createConceptSchema = z.object({
  name: z.string().trim().min(2).max(100),
  slug: slugSchema,
  domain: domainSchema,
  supportEmail: emailSchema,
  countryCodes: z.array(countryCodeSchema).min(1).max(50),
  defaultCountryCode: countryCodeSchema,
}).refine((c) => c.countryCodes.includes(c.defaultCountryCode), {
  message: 'default_country_not_in_countries',
  path: ['defaultCountryCode'],
});
export type CreateConceptPayload = z.infer<typeof createConceptSchema>;

// Updates derive from create (commandment 14) — domain/slug changes allowed
// for system admins only; refine re-applied in the service.
export const updateConceptSchema = z.object({
  name: z.string().trim().min(2).max(100),
  domain: domainSchema,
  supportEmail: emailSchema,
  countryCodes: z.array(countryCodeSchema).min(1).max(50),
  defaultCountryCode: countryCodeSchema,
}).partial();
export type UpdateConceptPayload = z.infer<typeof updateConceptSchema>;

export const updateConceptFeaturesSchema = z.object({
  features: z.partialRecord(featureKeyEnum, z.boolean()),
});
export type UpdateConceptFeaturesPayload = z.infer<typeof updateConceptFeaturesSchema>;

export const assignConceptAdminSchema = z.object({
  email: emailSchema,
  firstName: z.string().trim().min(1).max(100).optional(),
  lastName: z.string().trim().min(1).max(100).optional(),
});
export type AssignConceptAdminPayload = z.infer<typeof assignConceptAdminSchema>;

// System (platform) admin assignment — same shape, no concept scope.
export const assignPlatformAdminSchema = assignConceptAdminSchema;
export type AssignPlatformAdminPayload = z.infer<typeof assignPlatformAdminSchema>;

// Deactivate / reactivate an admin user (system admin only).
export const updateAdminStatusSchema = z.object({ status: z.enum(['active', 'deactivated']) });
export type UpdateAdminStatusPayload = z.infer<typeof updateAdminStatusSchema>;

export const conceptIdParamSchema = z.object({ conceptId: uuidSchema });
